import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  Compass, Search, BookHeart, Calendar, ShoppingCart,
  PenSquare, Settings, ChefHat, Heart,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';

// Desktop fixed sidebar per shared-docs/frontend-architecture.md: hidden md:flex
// fixed left-0 top-0 w-64 h-screen. Hidden on mobile where the bottom nav + mobile
// drawer (hamburger) take over.
export default function Sidebar() {
  const { categories, savedCount } = useApp();

  const navItems = [
    { to: '/', icon: Compass, label: 'Explore' },
    { to: '/browse', icon: Search, label: 'Browse Recipes' },
    { to: '/cookbook', icon: BookHeart, label: 'My Cookbook' },
    { to: '/meal-plan', icon: Calendar, label: 'Meal Plan' },
  ];

  const utilityItems = [
    { to: '/grocery', icon: ShoppingCart, label: 'Grocery List' },
    { to: '/submit', icon: PenSquare, label: 'Submit a Recipe' },
    { to: '/profile', icon: Settings, label: 'Settings' },
  ];

  const baseLink = 'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors';
  const inactive = 'text-gray-700 dark:text-night-text hover:bg-gray-100 dark:hover:bg-night-card';
  const active = 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400';

  return (
    <aside
      role="navigation"
      aria-label="Sidebar"
      className="hidden md:flex flex-col fixed left-0 top-0 w-64 h-screen bg-white dark:bg-night-surface border-r border-gray-100 dark:border-night-card overflow-y-auto"
    >
      <div className="px-5 py-5 border-b border-gray-100 dark:border-night-card flex items-center gap-2">
        <ChefHat size={28} className="text-primary-600" aria-hidden="true" />
        <span className="text-lg font-semibold text-gray-900 dark:text-night-text">Cooksmith</span>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1">
        {navItems.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            className={({ isActive }) => `${baseLink} ${isActive ? active : inactive}`}
          >
            <Icon size={20} aria-hidden="true" />
            <span>{label}</span>
          </NavLink>
        ))}

        <div className="border-t border-gray-100 dark:border-night-card my-3" />

        {utilityItems.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) => `${baseLink} ${isActive ? active : inactive}`}
          >
            <Icon size={20} aria-hidden="true" />
            <span>{label}</span>
          </NavLink>
        ))}

        <div className="border-t border-gray-100 dark:border-night-card my-3" />

        <h3 className="text-xs font-semibold text-gray-500 dark:text-night-muted uppercase tracking-wide px-3 mb-2">
          Quick Access
        </h3>
        <NavLink to="/cookbook" className={`${baseLink} ${inactive}`}>
          <Heart size={18} aria-hidden="true" />
          <span className="flex-1">My Favourites</span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-night-card text-gray-700 dark:text-night-text">
            {savedCount}
          </span>
        </NavLink>

        {categories.length > 0 && (
          <>
            <h3 className="text-xs font-semibold text-gray-500 dark:text-night-muted uppercase tracking-wide px-3 mt-4 mb-2">
              Categories
            </h3>
            {categories.map((cat) => (
              <NavLink
                key={cat.id}
                to={`/browse?category=${encodeURIComponent(cat.name)}`}
                className={`${baseLink} ${inactive} text-sm`}
              >
                <span className="w-2 h-2 rounded-full bg-primary-400" aria-hidden="true" />
                <span>{cat.name}</span>
              </NavLink>
            ))}
          </>
        )}
      </nav>
    </aside>
  );
}
